import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";

import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import type { EmptyModalState } from "../../components/modals/EmptyModal";
import type { SlideOverState } from "../../components/slideOver/SlideOver";
import type { ApiMessage, SelectOption } from "../../types/api";
import type { CategoryFilters, SubCategoriesResponse, SubCategorySummary } from "../../types/categories";

export function SubCategoriesViewModel(logout: () => void) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [subCategories, setSubCategories] = useState<SubCategoriesResponse>({
    current_page: 1, data: [], last_page: 1, per_page: 50, total: 0,
  });
  const [primaryCategoriesForDropdown, setPrimaryCategoriesForDropdown] = useState<SelectOption[]>([]);

  const defaultFilters: CategoryFilters = {
    primaryCategory: { value: -1, label: "All Primary Categories" },
  };
  const [filters, setFilters] = useState(defaultFilters);

  const [slideOver, setSlideOver] = useState<SlideOverState>({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title: string, subtitle = "Please try again.") => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getSubCategories = async function (search: string | null, page: number, filter: CategoryFilters) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };
    const callResult = await makePostApiCall<SubCategoriesResponse, typeof payload>(`products/subcategories`, payload);
    setLoading(false);
    if (callResult.success) {
      setSubCategories(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch sub categories");
    }
  };

  const getPrimaryCategoriesForDropdown = async function () {
    const callResult = await makePostApiCall<SelectOption[]>(`products/categories/dropdown`);
    if (callResult.success) {
      setPrimaryCategoriesForDropdown(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message || "Could not fetch categories");
    }
  };

  const resetFilters = () => {
    const filterReset = {
      primaryCategory: { value: -1, label: "All Primary Categories" },
    };
    setFilters(filterReset);
    getSubCategories(null, 1, filterReset);
  };

  const deleteSubCategory = async function (id: number) {
    const callResult = await makePostApiCall<ApiMessage>(`products/subcategories/delete/${id}`, {});
    if (callResult.success) {
      showSuccess(callResult.data?.message || "Sub category deleted");
      getSubCategories(null, 1, filters);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Could not delete sub category",
          callResult.data?.message || "Please try again."
        );
      }
    }
  };

  const askDeleteSubCategory = function (category: SubCategorySummary) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Delete sub category"
          buttonMessage="Delete"
          message={`Are you sure you want to delete "${category.name}"? This action cannot be undone.`}
          setOpen={setEmptyModelOpen}
          confirmAction={async () => {
            await deleteSubCategory(category.id);
            setEmptyModelOpen({ childComponent: null, open: false });
          }}
        />
      ),
      open: true,
    });
  };

  const primaryCategoryOptions = [
    { value: -1, label: "All Primary Categories" },
    ...primaryCategoriesForDropdown,
  ];

  const filterComponents = [
    <FilterSingleSelectDropdown
      key="primaryCategory"
      header="Primary Category"
      onChange={(value) =>
        setFilters({
          ...filters,
          primaryCategory: value ?? defaultFilters.primaryCategory,
        })
      }
      options={primaryCategoryOptions}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="primaryCategory"
      value={filters.primaryCategory}
    />,
  ];

  return {
    emptyModal,
    setEmptyModelOpen,
    getSubCategories,
    loading,
    subCategories,
    setSlideOver,
    slideOver,
    filterComponents,
    resetFilters,
    filters,
    getPrimaryCategoriesForDropdown,
    askDeleteSubCategory,
  };
}
